import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Fax from "pages/fax";
import ComposeFax from "pages/fax/composeFax";
import { useAuth0 } from "@auth0/auth0-react";
import Loader from "components/ui-component/loader/Loader";
import { useAppDispatch } from "redux/hooks";
import {
  clearCommonSlice,
  setAccessToken,
  setLoading,
  setUserInfo,
  useAccessToken,
  useIsError,
  useLoading,
} from "redux/slice/commanSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfoError from "pages/infoError";
import { clearSlice } from "redux/slice/faxSlice";
import TryAgainLogin from "pages/tryAgainLogin/tryAgainLogin";
import NewHeader from "components/layouts/NewHeader/NewHeader";
import { Col, Row } from "antd";
import { PageContent } from "components/layouts/MainLayout/MainLayout.style";
import NewSidebar from "components/layouts/NewSidebar/NewSidebar";
import AuthCallback from "pages/auth-callback";
import { backendLogout } from "API/backendLogout";
import { setTokenRefresher } from "API/tokenManager";

const MainLayout = () => {
  const {
    user,
    isAuthenticated,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [searchParams] = useSearchParams();
  const isIframe = searchParams.get("is_iframe");
  const urlAccessToken = searchParams.get("access_token");
  console.log("isIframe", isIframe);
  const [iframeState, setIframeState] = useState(isIframe);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const accessToken = useAccessToken();
  const location = useLocation();
  const pathName = location.pathname;
  const isError = useIsError();
  const loading = useLoading();

  const navigation = useNavigate();

  // Track if we're actually running in Teams (not just if SDK is loaded)
  const [isInTeams, setIsInTeams] = useState(false);
  // Track if we've completed checking for Teams context
  const [teamsCheckComplete, setTeamsCheckComplete] = useState(false);

  // Initialize Teams SDK if running in Teams
  useEffect(() => {
    const initializeTeams = async () => {
      try {
        // Check if microsoftTeams is available (loaded from script tag)
        if (window.microsoftTeams) {
          console.log('Teams SDK detected, checking if running in Teams...');

          // Try to initialize - this will fail if not in Teams context
          await window.microsoftTeams.app.initialize();
          console.log('Teams SDK initialized successfully');

          // Get Teams context for debugging
          const context = await window.microsoftTeams.app.getContext();
          console.log('Teams context:', context);

          // If we got here without error, we're actually in Teams
          setIsInTeams(true);
          setTeamsCheckComplete(true);

          // IMPORTANT: Always notify Teams that app loaded successfully
          window.microsoftTeams.app.notifySuccess();
          console.log('Teams notified of successful load');
        } else {
          console.log('Not running in Teams (microsoftTeams SDK not found)');
          setTeamsCheckComplete(true);
        }
      } catch (error) {
        console.log('Not running in Teams context (initialization failed):', error);
        // Don't notify Teams of failure - we're just not in Teams
        setIsInTeams(false);
        setTeamsCheckComplete(true);
      }
    };

    // Delay initialization slightly to ensure DOM is ready
    const timer = setTimeout(initializeTeams, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleTeamsAuth = () => {
    console.log('Starting Teams authentication popup...');

    // Build Auth0 authorization URL
    const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || '';
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || '';
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE || '';
    const redirectUri = `${window.location.origin}/auth-callback`;

    if (!auth0Domain || !clientId || !audience) {
      console.error('Missing Auth0 configuration');
      dispatch(setLoading(false));
      return;
    }

    // Generate cryptographically secure nonce for OAuth security
    const generateNonce = () => {
      const array = new Uint8Array(16);
      crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    };

    const authUrl = `https://${auth0Domain}/authorize?` +
      `response_type=token&` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=openid profile email&` +
      `audience=${encodeURIComponent(audience)}&` +
      `nonce=${generateNonce()}`;

    console.log('Opening auth popup:', authUrl);

    // Use Teams SDK to open authentication popup
    window.microsoftTeams!.authentication.authenticate({
      url: authUrl,
      width: 600,
      height: 535,
      successCallback: (result: string) => {
        console.log('Teams auth success:', result);
        try {
          const tokenData = JSON.parse(result);
          const token = tokenData.access_token;
          if (token) {
            dispatch(setAccessToken(token));
            localStorage.setItem("accessToken", token);
            dispatch(setLoading(false));
            // Force re-render to show authenticated content
            window.location.reload();
          }
        } catch (err) {
          console.error('Error parsing auth result:', err);
        }
      },
      failureCallback: (reason: string) => {
        console.error('Teams auth failed:', reason);
        dispatch(setLoading(false));
      }
    });
  };

  const handleUrlToken = (token: string) => {
    console.log('Teams mode: Using token from URL');
    dispatch(setAccessToken(token));
    localStorage.setItem("accessToken", token);
    dispatch(setLoading(false));
  };

  const handleLoginRequired = () => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("error");

    if (errorParam === "access_denied") {
      navigate(`/relogin${window.location.search}`);
      return;
    }

    // Check if actually running in Teams context (not just SDK loaded)
    if (isInTeams) {
      console.log('Login required in Teams - opening popup');
      handleTeamsAuth();
      return;
    }

    // Normal browser login
    console.log('Login required - using Auth0 redirect');
    loginWithRedirect();
    dispatch(setUserInfo(true));
  };

  const handleAccountNotFound = async () => {
    await backendLogout();
    logout();
    localStorage.clear();
    dispatch(clearSlice());
    dispatch(clearCommonSlice());
  };

  const handleOtherError = () => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("error");

    if (errorParam === "access_denied") {
      navigate(`/relogin${window.location.search}`);
    } else {
      handleAccountNotFound();
    }
  };

  const AuthHeader = async () => {
    try {
      // Handle token from URL (Teams scenario)
      if (urlAccessToken && isIframe) {
        handleUrlToken(urlAccessToken);
        return;
      }

      const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
      const accessToken: any = await getAccessTokenSilently({
        authorizationParams: {
          audience: audience,
          scope: "read:current_user",
        },
      });
      dispatch(setAccessToken(accessToken));
      localStorage.setItem("accessToken", accessToken);
    } catch (error: any) {
      console.log(error);

      // When the user isn't authenticated, any token error means they need
      // to log in — never trigger logout (which would cause a redirect loop).
      if (!isAuthenticated) {
        handleLoginRequired();
      } else if (error?.message === "We could not locate your OneCloud Account") {
        handleAccountNotFound();
      } else {
        handleOtherError();
      }
    }
  };

  // Register the token refresher so the axios interceptor can refresh on 401
  const refreshToken = useCallback(async () => {
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    const token = await getAccessTokenSilently({
      authorizationParams: { audience, scope: "read:current_user" },
    });
    dispatch(setAccessToken(token));
    localStorage.setItem("accessToken", token);
    return token;
  }, [getAccessTokenSilently, dispatch]);

  useEffect(() => {
    setTokenRefresher(refreshToken);
  }, [refreshToken]);

  // Proactively refresh the token on an interval (every 50 minutes)
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!isAuthenticated) return;

    refreshIntervalRef.current = setInterval(() => {
      refreshToken().catch((err) =>
        console.error("Proactive token refresh failed:", err)
      );
    }, 50 * 60 * 1000);

    return () => clearInterval(refreshIntervalRef.current);
  }, [isAuthenticated, refreshToken]);

  // Memoized check for compose-fax route
  const isComposeFaxRoute = useMemo(
    () => pathName === "/fax/compose-fax",
    [pathName]
  );

  useEffect(() => {
    if (isIframe) {
      setIframeState(isIframe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIframe]);

  useEffect(() => {
    // Only attempt authentication after we've determined if we're in Teams
    if (teamsCheckComplete) {
      AuthHeader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, user?.sub, isAuthenticated, teamsCheckComplete]);

  useEffect(() => {
    if (isError) {
      navigation("/invalid-user");
    } else if (pathName === "/invalid-user") {
      navigation("/");
    } else if (accessToken) {
      dispatch(setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, pathName]);

  return (
    <>
      {isAuthenticated && !!accessToken && (
        <>
          {!iframeState && <NewHeader />}
          <Row wrap={false}>
            <Col flex="none">{!isComposeFaxRoute && <NewSidebar />}</Col>
            <Col flex="auto">
              <PageContent>
                {loading ? (
                  <div className="h-[100vh]">
                    <Loader />
                  </div>
                ) :null }
                    <Routes>
                      <Route path="/invalid-user" element={<InfoError />} />
                      <Route path="/" element={<Fax />} />
                      <Route path="/fax/compose-fax" element={<ComposeFax />} />
                    </Routes>
              </PageContent>
            </Col>
          </Row>
        </>
      )}
      <Routes>
        <Route path="/relogin" element={<TryAgainLogin />} />
        <Route path="/auth-callback" element={<AuthCallback />} />
      </Routes>
    </>
  );
};
export default MainLayout;
